import {
    BankCustodian,
    CardCustomizationState,
    DebitCardCustomization,
    DebitCardCustomizationCreateRequest,
    DebitCardCustomizationUpdateRequest,
    PartnerCardCustomizationUpdateRequest,
} from "@/admin/types";
import {
    FetchState
} from "@/shared/types";
import Vue from "vue";
import * as bankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: CardCustomizationState = {
    cardCustomizationFetchState: FetchState.Uninitialized,
    cardCustomizations: [],
    partners: {}
};

const defaultPartnerItem = () => ({
    fetchState: FetchState.Uninitialized,
    customization: null
});

export const useCardCustomizationStore = defineStore("cardCustomization", {
    state: (): CardCustomizationState => (_.cloneDeep(initialState)),
    getters: {
        custodiansAllowingCustomization: state => [ BankCustodian.Avidia ],
        canCustomizeCards () {
            return (custodian: BankCustodian) =>
                this.custodiansAllowingCustomization.some(c => c === custodian);
        },
        isLoadingCardCustomizations: state => state.cardCustomizationFetchState === FetchState.Loading,
        didLoadCardCustomizations: state => state.cardCustomizationFetchState === FetchState.Loaded,
        isLoadingPartnerCardCustomization: state => (partnerId: number) => {
            const item = state.partners[partnerId];
            return item && item.fetchState === FetchState.Loading;
        },
        didLoadPartnerCardCustomization: state => (partnerId: number) => {
            const item = state.partners[partnerId];
            return item && item.fetchState === FetchState.Loaded;
        },
        partnerCardCustomization: state => (partnerId: number) => {
            const item = state.partners[partnerId];
            return item ? item.customization : null;
        },
    },
    actions: {
        setCardCustomizationFetchState (fetchState: FetchState) {
            this.cardCustomizationFetchState = fetchState;
        },
        setCardCustomizations (cardCustomizations: DebitCardCustomization[]) {
            this.cardCustomizations = cardCustomizations;
        },
        addCardCustomization (customization: DebitCardCustomization) {
            this.cardCustomizations.push(customization); // TODO: double-check this vuex reactivity is preserved here!
        },
        updateCardCustomization (customization: DebitCardCustomization) {
            const index = this.cardCustomizations
                .findIndex(cc => cc.customizationId && cc.customizationId === customization.customizationId);

            if (index < 0) {
                return;
            }

            Vue.set(this.cardCustomizations, index, customization);
        },

        setPartnerFetchState ({ partnerId, fetchState }: { partnerId: number, fetchState: FetchState }) {
            if (!this.partners[partnerId]) {
                Vue.set(this.partners, partnerId, defaultPartnerItem());
            }
            this.partners[partnerId].fetchState = fetchState;
        },

        setPartnerCustomization (
            { partnerId, customization }: { partnerId: number, customization: DebitCardCustomization }
        ) {
            if (!this.partners[partnerId]) {
                this.partners[partnerId] = defaultPartnerItem();
            }

            this.partners[partnerId].customization = customization;
        },

        loadCardCustomizations (custodian: BankCustodian): Promise<any> {
            if (this.cardCustomizationFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setCardCustomizationFetchState(FetchState.Loading);
            return bankService.getCardCustomizations(custodian)
                .then(response => {
                    this.setCardCustomizations(response.data);
                    this.setCardCustomizationFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setCardCustomizationFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        createCardCustomization (request: DebitCardCustomizationCreateRequest): Promise<any> {
            return bankService.createCardCustomization(request)
                .then(response => {
                    this.addCardCustomization(response.data);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        beginUpdateCardCustomization (request: DebitCardCustomizationUpdateRequest): Promise<any> {
            return bankService.updateCardCustomization(request)
                .then(response => {
                    this.updateCardCustomization(response.data);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        getPartnerCardCustomization (partnerId: number): Promise<any> {
            if (this.partners[partnerId] && this.partners[partnerId].fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setPartnerFetchState({ partnerId, fetchState: FetchState.Loading });
            return bankService.getPartnerCardCustomization(partnerId)
                .then(response => {
                    this.setPartnerCustomization({
                        partnerId,
                        customization: response.data
                    });

                    this.setPartnerFetchState({ partnerId, fetchState: FetchState.Loaded });
                })
                .catch(err => {
                    this.setPartnerFetchState({ partnerId, fetchState: FetchState.Uninitialized });
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        setPartnerCardCustomization (
            request: PartnerCardCustomizationUpdateRequest): Promise<any> {
            return bankService.setPartnerCardCustomization(request)
                .then(response => {
                    this.setPartnerCustomization({
                        partnerId: request.partnerId,
                        customization: response.data
                    });
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        }
    }
});
